import { useMemo, useRef } from "react";
import * as THREE from "three";
import { Billboard, useScroll } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import GateAndStairs from "../../GateAndStairs";
import { Cloud } from "../../Cloud";
import { Clouds } from "../../Clouds";
import { makeSphericalDistribute } from "../utils";
import {
  DEFAULT_GROUND_FOG_COLOR,
  DEFAULT_GROUND_FOG_OPACITY,
  DEFAULT_GROUND_FOG_POS_1,
  DEFAULT_GROUND_FOG_POS_2,
  DEFAULT_GROUND_FOG_ROT_1,
  DEFAULT_GROUND_FOG_ROT_2,
} from "../constants";
import { useSceneScalars } from "../../../hooks/useSceneScalars";

function makeGlowTexture(): THREE.Texture {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createRadialGradient(
    size / 2,
    size / 2,
    0,
    size / 2,
    size / 2,
    size / 2,
  );
  grad.addColorStop(0.0, "rgba(255, 248, 230, 0.35)");
  grad.addColorStop(0.2, "rgba(255, 240, 200, 0.22)");
  grad.addColorStop(0.45, "rgba(255, 225, 160, 0.1)");
  grad.addColorStop(0.75, "rgba(255, 215, 140, 0.03)");
  grad.addColorStop(1.0, "rgba(255, 215, 140, 0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function makeRayTexture(): THREE.Texture {
  const w = 64;
  const h = 512;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  // Horizontal gradient: soft on edges, brighter in center, fades out
  const grad = ctx.createLinearGradient(0, 0, w, 0);
  grad.addColorStop(0.0, "rgba(255, 240, 200, 0)");
  grad.addColorStop(0.5, "rgba(255, 245, 215, 0.55)");
  grad.addColorStop(1.0, "rgba(255, 240, 200, 0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
  // Vertical fade — fade out at top and bottom of the ray
  const fade = ctx.createLinearGradient(0, 0, 0, h);
  fade.addColorStop(0.0, "rgba(0, 0, 0, 1)");
  fade.addColorStop(0.15, "rgba(0, 0, 0, 0)");
  fade.addColorStop(0.85, "rgba(0, 0, 0, 0)");
  fade.addColorStop(1.0, "rgba(0, 0, 0, 1)");
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, w, h);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

type Props = {
  spherical: Float32Array;
  segments: number;
  active: boolean;
};

// Fan of god rays around Z. Wider angles, longer reach, no central ray
// (the portal glow already occupies the center).
const RAY_CONFIGS: Array<{
  rotZ: number;
  scale: [number, number];
  opacity: number;
}> = [
  { rotZ: 0.35, scale: [12, 140], opacity: 0.55 },
  { rotZ: -0.35, scale: [12, 140], opacity: 0.55 },
  { rotZ: 0.7, scale: [10, 130], opacity: 0.45 },
  { rotZ: -0.7, scale: [10, 130], opacity: 0.45 },
  { rotZ: 1.05, scale: [9, 120], opacity: 0.35 },
  { rotZ: -1.05, scale: [9, 120], opacity: 0.35 },
  { rotZ: 1.35, scale: [8, 110], opacity: 0.25 },
  { rotZ: -1.35, scale: [8, 110], opacity: 0.25 },
];

// Baked Leva values for the Home cloud panel (was: useControls("Home · Clouds"))
const HOME_CLOUDS = {
  activeOpacity: 1.0,
  volume: 160,
  growth: 200,
  smallestVolume: 0.5,
  bounds: 310,
  speed: 0.13,
  color: "#dedede",
  position: [0, 0, 0] as [number, number, number],
  scale: 1.05,
} as const;

// Baked Leva values for the Home lighting panel (was: useControls("Home · Lighting"))
const HOME_LIGHTING = {
  ambient: 1.2,
  hemisphere: 2.4,
  keyLight: 1.7,
  rimLight: 1.1,
} as const;

export function HomePart({ spherical, segments, active }: Props) {
  const cloud = HOME_CLOUDS;
  const light = HOME_LIGHTING;

  const opacity = active ? cloud.activeOpacity : 0.1;
  const glowTex = useMemo(() => makeGlowTexture(), []);
  const rayTex = useMemo(() => makeRayTexture(), []);
  const gateGroupRef = useRef<THREE.Group>(null);
  const rayMatRefs = useRef<(THREE.MeshBasicMaterial | null)[]>([]);
  const scroll = useScroll();
  const scalars = useSceneScalars();

  useFrame(({ clock }) => {
    if (!gateGroupRef.current) return;
    const t = Math.min(scroll.offset * scalars.tScale, scalars.tMax);
    gateGroupRef.current.visible = t < scalars.thresholds.gateHide;

    // Animate god ray opacity — slow pulsing with per-ray phase offset
    const elapsed = clock.elapsedTime;
    for (let i = 0; i < RAY_CONFIGS.length; i++) {
      const mat = rayMatRefs.current[i];
      if (!mat) continue;
      const base = RAY_CONFIGS[i].opacity;
      const phase = i * 0.8;
      const pulse = Math.sin(elapsed * 0.4 + phase) * 0.15;
      mat.opacity = base + base * pulse;
    }
  });

  return (
    <group>
      <ambientLight intensity={light.ambient} />
      <hemisphereLight
        color="#f0f6fc"
        groundColor="#8d99a8"
        intensity={light.hemisphere}
      />
      {/* Key light: from camera side (+X), slightly above */}
      <directionalLight
        intensity={light.keyLight}
        position={[40, 30, 20]}
        castShadow
      />
      {/* Rim light: from behind the gate, picks out silhouette of columns */}
      <directionalLight intensity={light.rimLight} position={[-20, 20, -30]} />
      <Clouds limit={segments} position={cloud.position} scale={cloud.scale}>
        <Cloud
          segments={segments}
          seed={1}
          volume={cloud.volume}
          growth={cloud.growth}
          smallestVolume={cloud.smallestVolume}
          bounds={cloud.bounds}
          opacity={opacity}
          speed={cloud.speed}
          color={cloud.color}
          concentrate="random"
          distribute={makeSphericalDistribute(spherical)}
        />
      </Clouds>

      <group visible={active}>
        <group ref={gateGroupRef} position={scalars.homeGateGroupOffset}>
          {/* Light portal inside the arch — core sphere + faked additive halo layers */}
          <group position={[0, 1, 0]}>
            <pointLight
              color="#ffeec9"
              intensity={250}
              distance={120}
              decay={2}
            />
            {/* Radial glow — billboarded plane with soft gradient texture */}
            <Billboard>
              <mesh>
                <planeGeometry args={[40, 40]} />
                <meshBasicMaterial
                  map={glowTex}
                  transparent
                  blending={THREE.AdditiveBlending}
                  depthWrite={false}
                  toneMapped={false}
                />
              </mesh>
            </Billboard>

            {/* God rays — fan of additive ray planes around the portal */}
            <Billboard>
              <group>
                {RAY_CONFIGS.map((cfg, i) => (
                  <mesh
                    // eslint-disable-next-line react/no-array-index-key
                    key={`ray-${i}`}
                    rotation={[0, 0, cfg.rotZ]}
                    scale={[cfg.scale[0], cfg.scale[1], 1]}
                  >
                    <planeGeometry args={[1, 1]} />
                    <meshBasicMaterial
                      ref={(el: THREE.MeshBasicMaterial | null) => {
                        rayMatRefs.current[i] = el;
                      }}
                      map={rayTex}
                      transparent
                      opacity={cfg.opacity}
                      blending={THREE.AdditiveBlending}
                      depthWrite={false}
                      toneMapped={false}
                    />
                  </mesh>
                ))}
              </group>
            </Billboard>
          </group>

          <GateAndStairs
            groundFogColor={DEFAULT_GROUND_FOG_COLOR}
            groundFogOpacity={DEFAULT_GROUND_FOG_OPACITY}
            groundFogPositions={[
              DEFAULT_GROUND_FOG_POS_1,
              DEFAULT_GROUND_FOG_POS_2,
            ]}
            groundFogRotations={[
              DEFAULT_GROUND_FOG_ROT_1,
              DEFAULT_GROUND_FOG_ROT_2,
            ]}
          />
        </group>
      </group>
    </group>
  );
}
